import { Army } from "@/domain/army";
import { getRandomArmyName } from "@/lib/name-generator";
import { ArmyImage, ArmyType } from "@/schema/latest";
import { useDatabase } from "@/ui/database-provider";
import { FormDialogV2 } from "@/ui/form-dialog";
import { InputsGroup } from "@/ui/inputs/group";
import { useDialogContext } from "@ariakit/react";
import { Failure, Success } from "@indietabletop/appkit";
import { useForm } from "../hooks/use-form";
import { useNavigate } from "../hooks/use-navigate";
import { InlineButton } from "../inline-button";
import { FormCheckboxField, FormTextAreaField, FormTextField } from "../inputs";
import { Link } from "../link";
import { MainImagePicker } from "../main-image-picker/MainImagePicker";
import { uploadStagedImages } from "../main-image-picker/uploadImages";
import { useStagedImage } from "../main-image-picker/use-staged-image";
import { interactiveText } from "../utils.css";
import { wrap } from "./wrap";

type SubmitValues = {
  name: string;
  description: string;
  images: ArmyImage[];
  type: ArmyType;
};

function ArmyFormDialog<T>(props: {
  heading: string;
  submitLabel: string;
  onSubmit: (armydata: SubmitValues) => Promise<Success<T> | Failure<string>>;
  onSuccess?: (successValue: T) => void;
  army?: Army;
}) {
  const { army } = props;

  const dialog = useDialogContext();
  const { stagedImage, setStagedImage } = useStagedImage(army?.mainImage ?? null);

  const { form, submitName } = useForm({
    defaultValues: {
      name: army?.data.name ?? "",
      description: army?.data.description ?? "",
      isCollection: army?.data.type === "COLLECTION",
    },

    async onSubmit({ values }) {
      const imagesOp = await uploadStagedImages([stagedImage]);

      if (imagesOp.isFailure) {
        return imagesOp;
      }

      const { isCollection, ...formValues } = values;

      return await props.onSubmit({
        ...formValues,
        images: imagesOp.valueOrNull() ?? [],
        type: isCollection ? "COLLECTION" : "ARMY",
      });
    },

    onSuccess(successValue) {
      props.onSuccess?.(successValue);
      dialog?.hide();
    },
  });

  return (
    <FormDialogV2
      heading={props.heading}
      submitLabel={props.submitLabel}
      form={form}
      formSubmitName={submitName}
    >
      <InputsGroup>
        <MainImagePicker stagedImage={stagedImage} setStagedImage={setStagedImage} />

        <FormTextField
          name={form.names.name}
          label="Name"
          placeholder="Eg. Blackmire Expedition"
          data-1p-ignore
          hint={
            <>
              Stuck?{" "}
              <InlineButton
                onClick={() => {
                  form.setValue(form.names.name, getRandomArmyName());
                }}
              >
                Generate a random name
              </InlineButton>
              .
            </>
          }
        />

        <FormTextAreaField
          name={form.names.description}
          label="Description"
          minRows={4}
          placeholder="Optionally add a backstory, how to play etc..."
        />

        <FormCheckboxField
          name={form.names.isCollection}
          label={
            <>
              Use as <strong>Collection</strong>.{" "}
              <Link href="~/help/collections" className={interactiveText}>
                Learn more.
              </Link>
            </>
          }
        />
      </InputsGroup>
    </FormDialogV2>
  );
}

export function CreateArmyDialog(props: { rulesetVersion: string }) {
  const navigate = useNavigate();
  const database = useDatabase();

  return (
    <ArmyFormDialog
      heading="Army"
      submitLabel="Create army"
      onSubmit={async (values) => {
        return await wrap(
          database.createArmy({ ...values, ruleset_version: props.rulesetVersion }),
        );
      }}
      onSuccess={(id) => {
        navigate(`~/army/a/${id}`);
      }}
    />
  );
}

export function EditArmyDialog(props: { army: Army }) {
  const { army } = props;
  const database = useDatabase();

  return (
    <ArmyFormDialog
      army={army}
      heading={`Edit ${army.shortTypeLabel}`}
      submitLabel="Save"
      onSubmit={async (values) => {
        return await wrap(database.updateArmy({ ...army.data, ...values }));
      }}
    />
  );
}
